/**
 * The route exports of the pages split out of Impact, Test vs control and the Cockpit (client
 * rule 3, DEC-87; package A 2026-09-30): `/strategic/override-outcomes`,
 * `/strategic/matched-control` and `/strategic/benefits` — and of Override outcomes ▸ By run,
 * split out of Benefits (issue 226, giro 3 «Dopo»): `/strategic/override-outcomes/by-run`.
 *
 * Each re-reads the operation its page reads, over the page's perimeter (the URL), and leaves an
 * absent figure as `missing`, never `0` — the same rules as `exports.ts`.
 */

import { callOperation, collectPages } from '@/api';
import { decisionSummary, getOverrideOutcomes, inseasonListRuns, listMeasurements, listOverridePairs } from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportRequest, Provenance, ProvenanceFilter } from '@/export';

import type { ExportScope } from '../manifest';
import { MEASURE_MATCHED_UPLIFT, newestPerMarket } from './benefits';
import { queueOverrideRows } from './by-run';
import { linesNarrowingQuery, readLinesNarrowing, readLinesScope } from './override-lines';
import { AS_OF_PARAM, COUNTRY_PARAM, decisionClassKey, readIsoDate, realisedAbsentKey, todayUtc } from './programme';
import { exportDisplay, exportDisplayOf } from '@/planning/words';
import { PROGRAMME_MEASURE_KEY, PROJECTION_BASIS_KEY } from './words';

export const OVERRIDES_EXPORT_ID = 'strategic.overrideOutcomes.classes';
export const MATCHED_EXPORT_ID = 'strategic.matchedControl.estimates';
export const BENEFITS_EXPORT_ID = 'strategic.benefits.measured';
export const OVERRIDES_BY_RUN_EXPORT_ID = 'strategic.overrideOutcomes.byRun';
export const OVERRIDE_LINES_EXPORT_ID = 'strategic.overrideOutcomes.lines';

const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
const numberCell = (value: number | null | undefined): ExportCell =>
  value === null || value === undefined ? missing : { kind: 'number', raw: value };
const integerCell = (value: number | null | undefined): ExportCell =>
  value === null || value === undefined ? missing : { kind: 'integer', raw: value };

function provenance(filters: readonly ProvenanceFilter[], measure: string, definition: string): Provenance {
  return {
    filters,
    hierarchyNode: NOT_PUBLISHED,
    timeWindow: NOT_PUBLISHED,
    scenario: NOT_PUBLISHED,
    planVersionId: NOT_PUBLISHED,
    measureDefinitions: [{ measure, definition, registerVersion: '' }],
    generatedAt: new Date().toISOString(),
    generatedBy: NO_PRINCIPAL,
    platformVersion: buildVersion(),
  };
}

const paramsOf = (scope?: ExportScope): URLSearchParams =>
  new URLSearchParams(scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search));

/** The override outcome per decision class, for the market and date in the URL — the page's first table. */
export async function overrideOutcomesExport(scope?: ExportScope): Promise<ExportRequest> {
  const params = paramsOf(scope);
  const country = params.get(COUNTRY_PARAM)?.trim() || null;
  const asOf = readIsoDate(params.get(AS_OF_PARAM)) ?? todayUtc(new Date());
  const classes = country === null
    ? []
    : (await callOperation(getOverrideOutcomes, { query: { country_key: country, as_of: asOf } })).classes ?? [];
  const rows: ExportCell[][] = classes.map((row) => [
    { kind: 'label', value: row.decisionClass, code: row.decisionClass },
    integerCell(row.decided),
    integerCell(row.overridden),
    integerCell(row.deferred),
    numberCell(row.rate),
    integerCell(row.pairs),
    integerCell(row.projectionArchived),
    row.realisedAbsentReason === null || row.realisedAbsentReason === undefined
      ? missing
      : { kind: 'label', value: row.realisedAbsentReason, code: row.realisedAbsentReason },
  ]);
  const filters: ProvenanceFilter[] = [
    ...(country === null ? [] : [{ field: 'country_key', operator: 'equals' as const, value: country }]),
    { field: 'as_of', operator: 'equals', value: asOf },
  ];
  return {
    id: OVERRIDES_EXPORT_ID,
    titleKey: 'strategic.overrides.title',
    columns: [
      // Codes in the screen's words in the workbook (usability audit E2); the CSV keeps them.
      { key: 'decision_class', headerKey: 'strategic.overrides.column.class', kind: 'label', display: exportDisplay(decisionClassKey), descriptionKey: 'strategic.columnGuide.overrides.class' },
      { key: 'decided', headerKey: 'strategic.overrides.column.decided', kind: 'integer', descriptionKey: 'strategic.help.overrideOutcomesPanel.colDecided' },
      { key: 'overridden', headerKey: 'strategic.overrides.column.overridden', kind: 'integer', descriptionKey: 'strategic.help.overrideOutcomesPanel.colOverridden' },
      { key: 'deferred', headerKey: 'strategic.overrides.column.deferred', kind: 'integer', descriptionKey: 'strategic.help.overrideOutcomesPanel.colDeferred' },
      { key: 'rate', headerKey: 'strategic.overrides.column.rate', kind: 'number', descriptionKey: 'strategic.columnGuide.overrides.rate' },
      { key: 'pairs', headerKey: 'strategic.overrides.column.pairs', kind: 'integer', descriptionKey: 'strategic.columnGuide.overrides.pairs' },
      { key: 'projection_archived', headerKey: 'strategic.overrides.column.projectionArchived', kind: 'integer', descriptionKey: 'strategic.help.overrideOutcomesPanel.colProjectionArchived' },
      {
        key: 'realised', headerKey: 'strategic.overrides.column.realised', kind: 'label',
        display: exportDisplay((code) => { const key = realisedAbsentKey(code); return key === 'strategic.overrides.realised.unrecognised' ? null : key; }),
        descriptionKey: 'strategic.help.overrideOutcomesPanel.colRealised',
      },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, 'programme.override_rate',
      'Per decision class over the governed window: lines the planner overrode (adjusted or rejected) over lines decided, as a ratio. Undefined — never zero — when no line was decided. The realised outcome is not measured per disposed line yet (bata-platform ADR 9015).'),
    perimeter: {
      filters,
      sort: [{ key: 'decision_class', direction: 'asc' }],
      visibleColumns: ['decision_class', 'decided', 'overridden', 'deferred', 'rate', 'pairs', 'projection_archived', 'realised'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * Override outcomes ▸ Lines: every decided line of the class in the URL (or of every class, no
 * `?class=`), for the market and date in the URL, each beside the projection it was written with —
 * the drill's table, every page of the read. A line with no archived projection is `missing`, never
 * 0; the realised outcome leaves as the platform's reason, never a number (ADR 9015).
 */
export async function overrideLinesExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const { country, asOf: named, decisionClass } = readLinesScope(search);
  // The file carries the lines the page lists: the narrowing in the address goes to the read, and into the provenance.
  const narrowing = readLinesNarrowing(search);
  const asOf = named ?? todayUtc(new Date());
  const lines = country === null
    ? []
    : await collectPages(listOverridePairs, {
      query: { country_key: country, as_of: asOf, ...(decisionClass === null ? {} : { decision_class: decisionClass }), ...linesNarrowingQuery(narrowing) },
    });
  const rows: ExportCell[][] = lines.map((row) => [
    { kind: 'text', value: row.decidedAt },
    { kind: 'label', value: row.decisionClass, code: row.decisionClass },
    { kind: 'label', value: row.disposition, code: row.disposition },
    { kind: 'label', value: row.overridden ? 'yes' : 'no', code: row.overridden ? 'yes' : 'no' },
    { kind: 'text', value: row.subjectId },
    row.runId === null || row.runId === undefined ? missing : { kind: 'text', value: row.runId },
    numberCell(row.projectedValue),
    row.currencyCode === null || row.currencyCode === undefined ? missing : { kind: 'text', value: row.currencyCode },
    row.projectionBasis === null || row.projectionBasis === undefined ? missing : { kind: 'label', value: row.projectionBasis, code: row.projectionBasis },
    row.realisedAbsentReason === null || row.realisedAbsentReason === undefined
      ? missing
      : { kind: 'label', value: row.realisedAbsentReason, code: row.realisedAbsentReason },
  ]);
  const filters: ProvenanceFilter[] = [
    ...(country === null ? [] : [{ field: 'country_key', operator: 'equals' as const, value: country }]),
    { field: 'as_of', operator: 'equals', value: asOf },
    ...(decisionClass === null ? [] : [{ field: 'decision_class', operator: 'equals' as const, value: decisionClass }]),
    ...(narrowing.overridden === null ? [] : [{ field: 'overridden', operator: 'equals' as const, value: String(narrowing.overridden) }]),
    ...(narrowing.projectionArchived === null ? [] : [{ field: 'projection_archived', operator: 'equals' as const, value: String(narrowing.projectionArchived) }]),
  ];
  return {
    id: OVERRIDE_LINES_EXPORT_ID,
    titleKey: 'strategic.lines.title',
    columns: [
      { key: 'decided_at', headerKey: 'strategic.overrides.column.decidedAt', kind: 'text', descriptionKey: 'strategic.columnGuide.lines.decidedAt' },
      { key: 'decision_class', headerKey: 'strategic.overrides.column.class', kind: 'label', display: exportDisplay(decisionClassKey), descriptionKey: 'strategic.help.overrideOutcomesPanel.colPairClass' },
      { key: 'disposition', headerKey: 'strategic.overrides.column.disposition', kind: 'label', descriptionKey: 'strategic.help.overrideOutcomesPanel.colDisposition' },
      { key: 'overridden', headerKey: 'strategic.overrides.column.overriddenLine', kind: 'label', descriptionKey: 'strategic.help.overrideOutcomesPanel.colOverriddenLine' },
      { key: 'subject_id', headerKey: 'strategic.overrides.column.subject', kind: 'text', descriptionKey: 'strategic.help.overrideOutcomesPanel.colSubject' },
      { key: 'run_id', headerKey: 'strategic.overrides.column.run', kind: 'text', descriptionKey: 'strategic.help.overrideOutcomesPanel.colRun' },
      { key: 'projected_value', headerKey: 'strategic.overrides.column.projected', kind: 'number', descriptionKey: 'strategic.columnGuide.lines.projected' },
      { key: 'currency_code', headerKey: 'strategic.lines.column.currency', kind: 'text', descriptionKey: 'strategic.columnGuide.lines.currency' },
      { key: 'projection_basis', headerKey: 'strategic.overrides.column.basis', kind: 'label', display: exportDisplayOf(PROJECTION_BASIS_KEY), descriptionKey: 'strategic.help.overrideOutcomesPanel.colBasis' },
      {
        key: 'realised', headerKey: 'strategic.overrides.column.realised', kind: 'label',
        display: exportDisplay((code) => { const key = realisedAbsentKey(code); return key === 'strategic.overrides.realised.unrecognised' ? null : key; }),
        descriptionKey: 'strategic.help.overrideOutcomesPanel.colPairRealised',
      },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, 'programme.override_pair',
      'Each line approved, adjusted or rejected in the governed window ending at the as-of date, beside the projection its recommendation was written with (GET /programme/override-outcomes/pairs, bata-platform ADR 9015). An override is an adjustment or a rejection; deferred lines are not listed. Missing — never zero — when no projection was archived. The realised outcome is not measured per disposed line yet.'),
    perimeter: {
      filters,
      sort: [{ key: 'decided_at', direction: 'asc' }],
      visibleColumns: ['decided_at', 'decision_class', 'disposition', 'overridden', 'subject_id', 'run_id', 'projected_value', 'currency_code', 'projection_basis', 'realised'],
      drillPath: decisionClass === null ? [] : [decisionClass],
      densityMode: 'compact',
    },
  };
}

/**
 * Override outcomes ▸ By run: per queue of the cycle run in the URL, the decision summary's
 * override figures — the page's table. A queue the platform publishes no figures for is `missing`,
 * never 0; a rate with nothing decided is `missing` too.
 */
export async function overridesByRunExport(scope?: ExportScope): Promise<ExportRequest> {
  const runId = paramsOf(scope).get('runId')?.trim() || null;
  const summary = runId === null ? null : await callOperation(decisionSummary, { path: { run_id: runId } });
  const rows: ExportCell[][] = summary === null ? [] : queueOverrideRows(summary).map((row) => {
    const r = row.reading;
    return [
      { kind: 'label', value: row.id, code: row.id },
      r.kind === 'rate' ? integerCell(r.decided) : r.kind === 'nothingDecided' ? integerCell(0) : missing,
      r.kind === 'rate' ? integerCell(r.overridden) : r.kind === 'nothingDecided' ? integerCell(0) : missing,
      r.kind === 'notPublished' ? missing : integerCell(r.deferred),
      r.kind === 'rate' ? numberCell(r.rate) : missing,
    ];
  });
  const filters: ProvenanceFilter[] = runId === null ? [] : [{ field: 'run_id', operator: 'equals', value: runId }];
  return {
    id: OVERRIDES_BY_RUN_EXPORT_ID,
    titleKey: 'strategic.byRun.title',
    columns: [
      { key: 'queue', headerKey: 'strategic.byRun.column.queue', kind: 'label', display: exportDisplayOf(QUEUE_KEY), descriptionKey: 'strategic.help.byRun.colQueue' },
      { key: 'decided', headerKey: 'strategic.overrides.column.decided', kind: 'integer', descriptionKey: 'strategic.help.byRun.colDecided' },
      { key: 'overridden', headerKey: 'strategic.overrides.column.overridden', kind: 'integer', descriptionKey: 'strategic.help.byRun.colOverridden' },
      { key: 'deferred', headerKey: 'strategic.overrides.column.deferred', kind: 'integer', descriptionKey: 'strategic.help.byRun.colDeferred' },
      { key: 'rate', headerKey: 'strategic.overrides.column.rate', kind: 'number', descriptionKey: 'strategic.columnGuide.byRun.rate' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, 'inseason.decision_summary.override',
      'Per queue of the cycle run (GET /inseason/runs/{run_id}/decision-summary, bata-platform ADR 6036): lines the planner overrode (adjusted or rejected) over lines decided, as a ratio; a deferral is not a decision. Undefined — never zero — when no line was decided; missing when the platform publishes no override figures.'),
    perimeter: {
      filters,
      sort: [],
      visibleColumns: ['queue', 'decided', 'overridden', 'deferred', 'rate'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The queue's words in the workbook; the CSV keeps the code. */
const QUEUE_KEY: Readonly<Record<string, string>> = {
  proposals: 'overview.decided.proposals',
  transfers: 'overview.decided.transfers',
  priceSteps: 'overview.decided.priceSteps',
};

/**
 * The measured side of the Benefits page: every programme measurement of the chosen run's market,
 * as published. The expected side is each module's run figures, which the Impact page's file
 * carries; a file that restated them here could disagree with it.
 */
export async function benefitsExport(scope?: ExportScope): Promise<ExportRequest> {
  const runId = paramsOf(scope).get('runId')?.trim() || null;
  const run = runId === null ? undefined : (await callOperation(inseasonListRuns, { query: {} })).items.find((row) => row.run_id === runId);
  const country = run?.country_key ?? null;
  const measurements = country === null
    ? []
    : (await collectPages(listMeasurements, { query: {} })).filter((row) => (row.countryKey ?? null) === country);
  const rows: ExportCell[][] = measurements.map((row) => [
    { kind: 'label', value: row.measure, code: row.measure },
    numberCell(row.value),
    numberCell(row.lowerBound),
    numberCell(row.upperBound),
    { kind: 'label', value: row.evidenceState, code: row.evidenceState },
    row.windowEnd === null || row.windowEnd === undefined ? missing : { kind: 'date', iso: row.windowEnd },
  ]);
  const filters: ProvenanceFilter[] = country === null ? [] : [{ field: 'country_key', operator: 'equals', value: country }];
  return {
    id: BENEFITS_EXPORT_ID,
    titleKey: 'overview.benefits.title',
    columns: [
      { key: 'measure', headerKey: 'strategic.benefits.column.measure', kind: 'label', display: exportDisplayOf(PROGRAMME_MEASURE_KEY), descriptionKey: 'strategic.columnGuide.benefits.measure' },
      { key: 'value', headerKey: 'strategic.matched.estimate', kind: 'number', descriptionKey: 'strategic.columnGuide.benefits.value' },
      { key: 'lower_bound', headerKey: 'strategic.matched.column.lower', kind: 'number', descriptionKey: 'strategic.columnGuide.benefits.lower' },
      { key: 'upper_bound', headerKey: 'strategic.matched.column.upper', kind: 'number', descriptionKey: 'strategic.columnGuide.benefits.upper' },
      { key: 'evidence_state', headerKey: 'strategic.matched.column.evidence', kind: 'label', descriptionKey: 'strategic.columnGuide.benefits.evidence' },
      { key: 'window_end', headerKey: 'strategic.matched.column.windowEnd', kind: 'date', descriptionKey: 'strategic.columnGuide.benefits.windowEnd' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, 'programme.measurement',
      'Each benefit measure the programme published for the market (GET /programme/measurements), with its evidence state. A modelled measure is not a measured one (DEC-11); an insufficient one carries no conclusion.'),
    perimeter: {
      filters,
      sort: [{ key: 'measure', direction: 'asc' }],
      visibleColumns: ['measure', 'value', 'lower_bound', 'upper_bound', 'evidence_state', 'window_end'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The newest matched-control estimate per market — the figures the page draws. */
export async function matchedControlExport(): Promise<ExportRequest> {
  const measurements = newestPerMarket(await collectPages(listMeasurements, { query: {} }), MEASURE_MATCHED_UPLIFT);
  const rows: ExportCell[][] = measurements.map((row) => [
    row.countryKey === null || row.countryKey === undefined ? missing : { kind: 'text', value: row.countryKey },
    numberCell(row.value),
    numberCell(row.lowerBound),
    numberCell(row.upperBound),
    integerCell(row.matchedPairs),
    { kind: 'label', value: row.evidenceState, code: row.evidenceState },
    row.windowEnd === null || row.windowEnd === undefined ? missing : { kind: 'date', iso: row.windowEnd },
  ]);
  const filters: ProvenanceFilter[] = [{ field: 'measure', operator: 'equals', value: MEASURE_MATCHED_UPLIFT }];
  return {
    id: MATCHED_EXPORT_ID,
    titleKey: 'strategic.matched.title',
    columns: [
      { key: 'country_key', headerKey: 'strategic.column.country', kind: 'text', descriptionKey: 'strategic.columnGuide.matched.country' },
      { key: 'value', headerKey: 'strategic.matched.estimate', kind: 'number', descriptionKey: 'strategic.columnGuide.matched.value' },
      { key: 'lower_bound', headerKey: 'strategic.matched.column.lower', kind: 'number', descriptionKey: 'strategic.columnGuide.matched.lower' },
      { key: 'upper_bound', headerKey: 'strategic.matched.column.upper', kind: 'number', descriptionKey: 'strategic.columnGuide.matched.upper' },
      { key: 'matched_pairs', headerKey: 'strategic.matched.column.pairs', kind: 'integer', descriptionKey: 'strategic.columnGuide.matched.pairs' },
      { key: 'evidence_state', headerKey: 'strategic.matched.column.evidence', kind: 'label', descriptionKey: 'strategic.columnGuide.benefits.evidence' },
      { key: 'window_end', headerKey: 'strategic.matched.column.windowEnd', kind: 'date', descriptionKey: 'strategic.columnGuide.benefits.windowEnd' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, MEASURE_MATCHED_UPLIFT,
      'Difference in differences between pilot doors and control doors matched inside their stratum, newest window per market (bata-platform ADR 9008). Undefined — never zero — while the evidence is insufficient.'),
    perimeter: {
      filters,
      sort: [{ key: 'country_key', direction: 'asc' }],
      visibleColumns: ['country_key', 'value', 'lower_bound', 'upper_bound', 'matched_pairs', 'evidence_state', 'window_end'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
